import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { getTodayDateString } from '@/lib/utils';
import { getSetting } from '@/features/admin/api/settings';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import { useCreateJobCost } from '../hooks/useCreateJobCost';
import { useUpdateJobCost } from '../hooks/useUpdateJobCost';
import { useCostCategories } from '../hooks/useCostCategories';
import { useJobEntryDuplicateCheck } from '../hooks/useJobEntryDuplicateCheck';
import { uploadCostImages } from '../api/costImages';
import { CostImagesSection } from './CostImagesSection';
import { DuplicateEntryWarningDialog } from './DuplicateEntryWarningDialog';
import type { JobCost } from '../types/jobCost';
import type { PotentialJobEntryDuplicate } from '../api/checkJobEntryDuplicates';

interface CostEntryFormProps {
  jobId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingCost?: JobCost | null;
  isForecast: boolean;
}

export function CostEntryForm({
  jobId,
  isOpen,
  onClose,
  onSuccess,
  editingCost,
  isForecast,
}: CostEntryFormProps) {
  const { hasPermission } = useAuth();
  const canEditTaxRate = hasPermission(Permissions.SettingsUpdate);
  const { data: categories } = useCostCategories();
  const { create, isCreating, error: createError } = useCreateJobCost();
  const { update, isUpdating, error: updateError } = useUpdateJobCost();
  const { checkForCostDuplicates, isChecking } = useJobEntryDuplicateCheck();

  // Default tax rate from system settings
  const defaultTaxRate = useRef('7.5');
  useEffect(() => {
    getSetting('TaxRate')
      .then((setting) => {
        const rate = parseFloat(setting.value) * 100;
        if (!isNaN(rate)) defaultTaxRate.current = rate.toString();
      })
      .catch(() => {}); // fallback to default
  }, []);

  // Duplicate detection state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicates, setDuplicates] = useState<PotentialJobEntryDuplicate[]>([]);
  const [duplicatesAcknowledged, setDuplicatesAcknowledged] = useState(false);

  // Form state
  const [costCategoryId, setCostCategoryId] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [taxAmountManuallyEdited, setTaxAmountManuallyEdited] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const error = createError || updateError || validationError;
  const isSubmitting = isCreating || isUpdating || isChecking || isUploadingFiles;

  // Initialize form when dialog opens or editingCost changes
  useEffect(() => {
    // This effect intentionally sets state to initialize/reset the form based on props.
    // This is a valid use case for form initialization and doesn't cause cascading renders.
    if (isOpen) {
      if (editingCost) {
        // Populate form with existing cost data
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCostCategoryId(editingCost.costCategoryId.toString());

        setPaymentDate(editingCost.paymentDate.split('T')[0]);

        setCheckNumber(editingCost.checkNumber || '');

        setPaidTo(editingCost.paidTo);

        setDescription(editingCost.description);

        setAmount(editingCost.amount.toString());

        setTaxRate((editingCost.taxRate * 100).toString());

        setTaxAmount(editingCost.taxAmount.toString());

        setTaxAmountManuallyEdited(false);
      } else {
        // Reset form for new cost
        setCostCategoryId('');
        setPaymentDate(getTodayDateString());
        setCheckNumber('');
        setPaidTo('');
        setDescription('');
        setAmount('');
        setTaxRate(defaultTaxRate.current);
        setTaxAmount('');
        setTaxAmountManuallyEdited(false);
      }

      setValidationError('');
      setDuplicatesAcknowledged(false);
      setDuplicates([]);
      setPendingFiles([]);
    }
  }, [isOpen, editingCost]);

  // Auto-calculate tax amount when amount or tax rate changes
  useEffect(() => {
    // This effect intentionally sets state to auto-calculate tax based on amount and rate.
    // This is a valid use case for derived state calculation.
    if (!taxAmountManuallyEdited) {
      const amountNum = parseFloat(amount);
      const rateNum = parseFloat(taxRate);
      if (!isNaN(amountNum) && !isNaN(rateNum) && amountNum > 0 && rateNum >= 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTaxAmount((amountNum * (rateNum / 100)).toFixed(2));
      } else {
        setTaxAmount('0');
      }
    }
  }, [amount, taxRate, taxAmountManuallyEdited]);

  // Get the current tax amount (either manual or calculated)
  const calculatedTaxAmount = taxAmount || '0';

  // Update tax rate when category changes
  const handleCategoryChange = (value: string) => {
    setCostCategoryId(value);

    const category = categories.find((c) => c.id.toString() === value);
    if (category?.applyTaxByDefault) {
      setTaxRate(defaultTaxRate.current);
    } else {
      setTaxRate('0');
    }
    // Reset manual edit flag so auto-calculation kicks in
    setTaxAmountManuallyEdited(false);
  };

  // Handle manual tax amount change
  const handleTaxAmountChange = (value: string) => {
    setTaxAmount(value);
    setTaxAmountManuallyEdited(true);
  };

  const validateForm = (): boolean => {
    if (!costCategoryId) {
      setValidationError('Category is required');
      return false;
    }

    if (!paymentDate) {
      setValidationError('Payment date is required');
      return false;
    }

    if (!paidTo.trim()) {
      setValidationError('Paid To is required');
      return false;
    }

    if (paidTo.length > 100) {
      setValidationError('Paid To must be 100 characters or less');
      return false;
    }

    if (!description.trim()) {
      setValidationError('Description is required');
      return false;
    }

    if (description.length > 500) {
      setValidationError('Description must be 500 characters or less');
      return false;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setValidationError('Amount must be greater than 0');
      return false;
    }

    if (canEditTaxRate) {
      const rateNum = parseFloat(taxRate);
      if (isNaN(rateNum) || rateNum < 0 || rateNum > 100) {
        setValidationError('Tax rate must be between 0 and 100');
        return false;
      }

      const taxNum = parseFloat(calculatedTaxAmount);
      if (isNaN(taxNum) || taxNum < 0) {
        setValidationError('Tax amount must be 0 or greater');
        return false;
      }
    }

    if (checkNumber && checkNumber.length > 50) {
      setValidationError('Check number must be 50 characters or less');
      return false;
    }

    return true;
  };

  const performSave = async () => {
    const data = {
      costCategoryId: parseInt(costCategoryId, 10),
      isForecast,
      paymentDate: new Date(paymentDate).toISOString(),
      checkNumber: checkNumber.trim() || undefined,
      paidTo: paidTo.trim(),
      description: description.trim(),
      amount: parseFloat(amount),
      ...(canEditTaxRate && taxRate !== '' && {
        taxRate: parseFloat(taxRate) / 100, // Convert percentage to decimal
      }),
      ...(canEditTaxRate && taxAmountManuallyEdited && {
        taxAmount: parseFloat(calculatedTaxAmount),
      }),
    };

    let result;
    if (editingCost) {
      result = await update(jobId, editingCost.id, data);
    } else {
      result = await create(jobId, data);
    }

    if (result) {
      // Upload pending files after successful creation
      if (!editingCost && pendingFiles.length > 0) {
        setIsUploadingFiles(true);
        try {
          await uploadCostImages(jobId, result.id, pendingFiles);
        } catch (err) {
          console.error('Failed to upload images after cost creation:', err);
        } finally {
          setIsUploadingFiles(false);
        }
      }
      onSuccess();
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!validateForm()) {
      return;
    }

    // If duplicates were already acknowledged, proceed with save
    if (duplicatesAcknowledged) {
      await performSave();
      return;
    }

    // Check for duplicates before saving
    const duplicateResult = await checkForCostDuplicates(jobId, {
      amount: parseFloat(amount),
      paidTo: paidTo.trim(),
      paymentDate: new Date(paymentDate).toISOString(),
      excludeCostId: editingCost?.id,
    });

    if (duplicateResult.hasDuplicates) {
      setDuplicates(duplicateResult.duplicates);
      setShowDuplicateDialog(true);
      return;
    }

    // No duplicates, proceed with save
    await performSave();
  };

  const handleProceedWithDuplicates = async () => {
    setDuplicatesAcknowledged(true);
    setShowDuplicateDialog(false);
    await performSave();
  };

  const handleCancelDuplicates = () => {
    setShowDuplicateDialog(false);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingCost ? 'Edit Cost' : `Add ${isForecast ? 'Forecasted' : 'Actual'} Cost`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <ErrorAlert error={error} />

          <div className="space-y-2">
            <Label htmlFor="category">
              Category <span className="text-red-500">*</span>
            </Label>
            <Select
              id="category"
              value={costCategoryId}
              onChange={(e) => handleCategoryChange(e.target.value)}
              required
              autoFocus
            >
              <option value="">Select category</option>
              {categories
                .filter((c) => c.isActive)
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((category) => (
                  <option key={category.id} value={category.id.toString()}>
                    {category.name}
                  </option>
                ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="paymentDate">
                Payment Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="paymentDate"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="checkNumber">Check Number</Label>
              <Input
                id="checkNumber"
                type="text"
                placeholder="Optional"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                maxLength={50}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="paidTo">
              Paid To <span className="text-red-500">*</span>
            </Label>
            <Input
              id="paidTo"
              type="text"
              placeholder="Company or person name"
              value={paidTo}
              onChange={(e) => setPaidTo(e.target.value)}
              maxLength={100}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Enter cost description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              required
            />
            <p className="text-xs text-gray-500">{description.length} / 500 characters</p>
          </div>

          <div className={`grid ${canEditTaxRate ? 'grid-cols-3' : 'grid-cols-1'} gap-4`}>
            <div className="space-y-2">
              <Label htmlFor="amount">
                Amount <span className="text-red-500">*</span>
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>

            {canEditTaxRate && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="taxRate">Tax Rate %</Label>
                  <Input
                    id="taxRate"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="0.00"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="taxAmount">Tax Amount</Label>
                  <Input
                    id="taxAmount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={calculatedTaxAmount}
                    onChange={(e) => handleTaxAmountChange(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : editingCost ? 'Update Cost' : 'Add Cost'}
            </Button>
          </div>

          {/* Image section */}
          <div className="border-t border-gray-200 my-4 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Receipt Images</h3>
            {editingCost ? (
              <CostImagesSection jobId={jobId} costId={editingCost.id} canDelete={true} />
            ) : (
              <>
                <ImageUpload
                  onUpload={async (files) => setPendingFiles((prev) => [...prev, ...files])}
                  isUploading={false}
                  compact
                  immediate
                />
                {pendingFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-muted-foreground">
                      {pendingFiles.length} file(s) will be uploaded on save
                    </p>
                    {pendingFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between text-xs bg-gray-50 px-2 py-1 rounded"
                      >
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          className="ml-2 text-muted-foreground hover:text-foreground"
                          onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </form>
      </DialogContent>

      {/* Duplicate Warning Dialog */}
      <DuplicateEntryWarningDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        entryType="Cost"
        duplicates={duplicates}
        onProceed={handleProceedWithDuplicates}
        onCancel={handleCancelDuplicates}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
}
